import { computed } from 'vue'
import { RISK_RANK } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/riskRank'
import { ABOVE_SLOPE_FLOOR_LEVEL } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/aboveSlopeFloorLevel'
import type * as Step2Type from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step2'
import type { RiskRank } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/common/riskRank.ts'
import type { AboveSlopeFloorLevel } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/aboveSlopeFloorLevel'

/**
 * 「建築物全体又は一部の傾斜」の選択値（1〜6）と内部データ（`riskRank`:'1'〜'3', `aboveSlopeFloorLevel`:'1','2'）を
 * 双方向にマッピング
 *
 * @param formattedDatasets - フォーマット済みのデータセット
 * @returns 傾斜選択値を管理する computed オブジェクト
 * @remarks
 * 選択値と内部データのマッピング:
 * - 選択値 -1: 未選択
 * - 選択値 1-3: 傾斜を生じた階の上の階数が1階以下、リスクランク 1-3
 * - 選択値 4-6: 傾斜を生じた階の上の階数が2階以上、リスクランク 1-3
 */
export function useSlopeSelected(formattedDatasets: {
  value: Record<string, Step2Type.SlopeItemObject>
}) {
  const slopeSelected = computed<string>({
    get: () => {
      const slope = formattedDatasets.value['slope'] as Step2Type.SlopeItemObject | undefined
      // slopeデータが存在しない場合は-1を返す
      if (!slope) return '-1'
      const risk = slope.riskRank
      const above = slope.aboveSlopeFloorLevel
      // 未選択（定数'-1'）の場合は -1 を返す
      if (risk === RISK_RANK.UNSELECTED) return '-1'
      //above が TWO_OR_MORE の場合、riskRankに3を加算して返す
      if (above === ABOVE_SLOPE_FLOOR_LEVEL.TWO_OR_MORE) {
        return String(Number(risk) + 3)
      }
      //  上記以外（ONE_OR_LESS 等）はそのまま riskRank を返す（1〜3）
      return String(risk)
    },

    set: val => {
      const selectedValue = Number(val)
      if (!formattedDatasets.value['slope']) return
      const slope = formattedDatasets.value['slope'] as Step2Type.SlopeItemObject
      // valueが-1の場合、riskRankにUNSELECTEDをセット
      if (selectedValue == -1) {
        slope.riskRank = RISK_RANK.UNSELECTED as RiskRank
        return
      }
      // value=1〜3の場合、ABOVE_SLOPE_FLOOR_LEVEL.ONE_OR_LESSとriskRankに1〜3をセット
      // value=4〜6の場合、ABOVE_SLOPE_FLOOR_LEVEL.TWO_OR_MOREとriskRankに1〜3をセット
      if (selectedValue >= 1 && selectedValue <= 3) {
        slope.aboveSlopeFloorLevel = ABOVE_SLOPE_FLOOR_LEVEL.ONE_OR_LESS as AboveSlopeFloorLevel
        slope.riskRank = String(selectedValue) as RiskRank
      } else if (selectedValue >= 4 && selectedValue <= 6) {
        slope.aboveSlopeFloorLevel = ABOVE_SLOPE_FLOOR_LEVEL.TWO_OR_MORE as AboveSlopeFloorLevel
        slope.riskRank = String(selectedValue - 3) as RiskRank
      }
    }
  })

  return { slopeSelected }
}
